import * as React from 'react'
import { FormikHelpers } from 'formik'

import {
  AsyncStatus,
  AutoVerificationType,
  EnrollAdditionalAutoProps,
} from '@ally/transmitigator'
import { Box, Button, ButtonGroup, Space } from '@ally/metronome-ui'
import { AccountNumber, Form, Forms, VINHullId } from '@ally/forms'
import { LoginHeading } from '@ally/ally-login'

import { useTransmit } from '../../TransmitContext'
import { CIAMErrorBanner, InputWrapper, StyledTextBody } from '../Shared'
import { FindAccountNumberModal, FindVINModal } from '../../Modals'

interface VerifyAutoInputFormProps {
  formId: string
  status: AsyncStatus
  type: AutoVerificationType
  navigateToAutoTypeSelect: () => void
}

export const VerifyAutoInputForm: React.FC<VerifyAutoInputFormProps> = ({
  formId,
  status,
  type,
  navigateToAutoTypeSelect,
}) => {
  return (
    <Form id={formId}>
      <InputWrapper>
        {type === 'accountNumber' && (
          <AccountNumber
            data-testid="account-number-input"
            name="inputValue"
            required
          />
        )}
        {type === 'vin' && (
          <VINHullId data-testid="vin-input" name="inputValue" required />
        )}
      </InputWrapper>

      <Box mt="sm" mb="xl">
        {type === 'accountNumber' && <FindAccountNumberModal />}
        {type === 'vin' && <FindVINModal />}
      </Box>

      <ButtonGroup>
        <Button
          data-testid="continue-button"
          fullWidthMd
          loading={status === 'Loading'}
          text="Continue"
          type="submit"
          variant="primary"
          allytmln="continue"
        />

        <Button
          data-testid="back-button"
          fullWidthMd
          onClick={navigateToAutoTypeSelect}
          text="Back"
          type="button"
          variant="link"
          allytmln="back"
        />
      </ButtonGroup>
    </Form>
  )
}

interface VerifyAutoInputFormValues {
  inputValue: string
}

export const VerifyAutoInputContent: React.FC<EnrollAdditionalAutoProps> = ({
  onComplete,
  navigateToAutoTypeSelect,
  status,
  type,
  error,
}) => {
  const formId = 'verify-auto-input-form'
  const initialValues = { inputValue: '' }
  const handleSubmit = (
    { inputValue }: VerifyAutoInputFormValues,
    { setSubmitting }: FormikHelpers<VerifyAutoInputFormValues>,
  ): void => {
    onComplete({
      [type]: inputValue,
    })

    // Since onComplete does not return a promise, we need to immediately reset the
    // isSubmitting flag in order to be able to resubmit the form in case of error
    setSubmitting(false)
  }

  return (
    <>
      <LoginHeading title="We need to make sure it's you." />

      <Space mb="md">
        <StyledTextBody size="sm" tag="p">
          Confirm your auto account by entering your 12-digit account number
          (which can be found on a recent billing statement) or your VIN/Hull
          ID.
        </StyledTextBody>
      </Space>

      <CIAMErrorBanner error={error} />

      <Forms
        formId={formId}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <VerifyAutoInputForm
          formId={formId}
          status={status}
          type={type}
          navigateToAutoTypeSelect={navigateToAutoTypeSelect}
        />
      </Forms>
    </>
  )
}

export const VerifyAutoInput: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'EnrollAutoInput') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <VerifyAutoInputContent {...activeScreen.payload} />
}
